import { useMemo } from 'react';
import { useGetBuyListQuoteComparison } from '~/api/buyList';
import { countOpenRequestsForQuote } from '~/utils/requestForQuoteClosed';
import { negotiationContextsFor } from '~/pages/rfq-view/sections/quoteNegotiation';
import {
  aggregateRequestsForQuote,
  findPlatformProductLengthIdsInSellerQuotes,
  type ComparableRequestForQuote,
} from './aggregateRequests';

const NO_REQUESTS_FOR_QUOTE: ComparableRequestForQuote[] = [];

export type BuyListQuoteComparisons = ReturnType<
  typeof useBuyListQuoteComparisons
>;

export function useBuyListQuoteComparisons(
  buyListId: string,
  options?: { enabled?: boolean }
) {
  const query = useGetBuyListQuoteComparison(buyListId, options);
  const requestsForQuote =
    query.data?.buyListRequestForQuotes.content ?? NO_REQUESTS_FOR_QUOTE;
  const totalRequestsForQuote =
    query.data?.buyListRequestForQuotes.paging.totalElements ??
    requestsForQuote.length;
  const comparisons = useMemo(
    () => aggregateRequestsForQuote(requestsForQuote, buyListId),
    [requestsForQuote, buyListId]
  );
  const platformProductLengthIdsInSellerQuotes = useMemo(
    () => findPlatformProductLengthIdsInSellerQuotes(comparisons),
    [comparisons]
  );
  const loadedRequestForQuoteIds = useMemo(
    () => new Set(requestsForQuote.map(({ id }) => id)),
    [requestsForQuote]
  );
  const negotiationByRequestForQuoteId = useMemo(
    () => negotiationContextsFor(requestsForQuote),
    [requestsForQuote]
  );

  return {
    isLoading: query.isLoading,
    error: query.error,
    comparisons,
    platformProductLengthIdsInSellerQuotes,
    negotiationByRequestForQuoteId,
    loadedRequestForQuoteIds,
    shownRequestsForQuote: requestsForQuote.length,
    totalRequestsForQuote,
    areAllRequestsForQuoteLoaded:
      query.isSuccess && requestsForQuote.length >= totalRequestsForQuote,
    openRequestsForQuote: countOpenRequestsForQuote(requestsForQuote),
  };
}
